import React from "react";
import { useAppSelector } from "../../../store";
import { groupSummaries } from "../../../store/profiling/selectors";
import type { CycleEntry } from "../../../store/profiling/types";
import { tracesInWindow } from "../../../store/tracing/selectors";
import type { Trace } from "../../../store/tracing/types";
import { formatDate } from "../../../shared/utilities";
import { traceKindLabel, traceSourceLabel } from "../traceFormat";

/** Compact one-line rendering of a trace value (old/new/error). */
function formatValue(v: unknown): string {
    if (v === undefined) return "—";
    try {
        const s = typeof v === "string" ? v : JSON.stringify(v);
        if (s == null) return String(v);
        return s.length > 80 ? `${s.slice(0, 80)}…` : s;
    } catch {
        return String(v);
    }
}

function TriggerRow({ trace }: { trace: Trace }) {
    const { event } = trace;
    const isError = event.kind === "DatasourceErrorOccurred";
    return (
        <div className={`trigger-row${isError ? " error" : ""}`}>
            <span className="trigger-time">{formatDate(trace.timestamp, "HH:mm:ss.SSS")}</span>
            <span className="trigger-kind">{traceKindLabel(event.kind)}</span>
            {event["name"] ? <span className="trigger-name">{event["name"]}</span> : null}
            <span className="trigger-detail">
                {isError
                    ? formatValue((event as any).error)
                    : <>{formatValue(event["oldValue"])} <span className="arrow">→</span> {formatValue(event["newValue"])}</>}
            </span>
            <span className="trigger-source">{traceSourceLabel(event.source)}</span>
        </div>
    );
}

/**
 * The recorded events that likely *caused* the selected render pass: traces whose `perfTime` falls in the
 * window between the previous pass's start and this pass's start (`(T_prev, T]`).  Requires tracing to be
 * on — which it is whenever profiling is on (the toggles are coupled).
 */
export function TriggeredBy({ selectedCycle }: { selectedCycle: CycleEntry | undefined }) {
    const profiling = useAppSelector(s => s.profiling);
    const traces = useAppSelector(s => s.tracing.traces);

    const causing = React.useMemo(() => {
        if (!selectedCycle) return [];
        const groups = groupSummaries(profiling).toSorted((a, b) => a.startTime - b.startTime);
        const i = groups.findIndex(g => g.rootCycleId === selectedCycle.groupId);
        if (i < 0) return [];
        const thisStart = groups[i].startTime;
        const prevStart = i > 0 ? groups[i - 1].startTime : -Infinity;
        return tracesInWindow(traces, prevStart, thisStart);
    }, [profiling, traces, selectedCycle]);

    if (!selectedCycle) return null;

    return (
        <div className="triggered-by">
            <div className="triggered-by-title">Triggered by <span className="count">{causing.length}</span></div>
            {causing.length === 0 ? (
                <div className="triggered-by-empty">No recorded events precede this pass (enable tracing / interact with the canvas).</div>
            ) : (
                <div className="trigger-list">
                    {causing.map((t, i) => <TriggerRow key={i} trace={t} />)}
                </div>
            )}
        </div>
    );
}

export default React.memo(TriggeredBy);
